import { getContrastText } from './helpers';

// Reusable dancing/idle SVG character. Color comes from the team's color
// in teamNamesColors. Pose and dance animation are selected by the caller.
// DANCES: 'bounce' | 'sway' | 'spin' | 'wiggle' | 'idle'
// POSES:  'armsUp' | 'armsOut' | 'crown' | 'sad' | 'waving' | 'neutral' | 'composed'
//   'composed' renders a non-smiling, non-sad face (straight eyes,
//   straight mouth, dim cheeks) — used by SecondPlace where the team
//   placed runner-up. Arms fall through to the relaxed default.
// FLAG_HAND: 'left' | 'right' — when paired characters carry the team
//   flag, the inner-side hand would clash with the partner, so callers
//   pass 'right' on the right-hand member of the pair so the flag drapes
//   outward. The arm reaching the pole is overridden regardless of pose.
export default function Character({
  color = '#2F4166',
  dance = 'bounce',
  pose = 'armsUp',
  teamName = '',
  showFlag = false,
  flagHand = 'left',
  flagWaving = true,
  droppingFlag = false,
  running = false,
  size = 'medium'
}) {
  const dimensions = size === 'small'
    ? { width: 120, height: 160 }
    : size === 'large'
      ? { width: 180, height: 240 }
      : { width: 140, height: 190 };

  const armsUp = pose === 'armsUp' || pose === 'crown';
  const armsOut = pose === 'armsOut';
  const isSad = pose === 'sad';
  const isWaving = pose === 'waving';
  const isComposed = pose === 'composed';

  const flagOnRight = showFlag && teamName && flagHand === 'right';
  const flagOnLeft = showFlag && teamName && !flagOnRight;

  const renderRightArm = () => {
    if (flagOnRight) return (
      <>
        <path d="M 140 95 Q 152 85 155 72" stroke="#1a1a1a" strokeWidth="3" fill="none" strokeLinecap="round" />
        <circle cx="155" cy="70" r="5" fill="#1a1a1a" />
      </>
    );
    if (armsUp) return (
      <>
        <path d="M 140 95 Q 160 70 165 40" stroke="#1a1a1a" strokeWidth="3" fill="none" strokeLinecap="round" />
        <circle cx="165" cy="38" r="5" fill="#1a1a1a" />
      </>
    );
    if (armsOut) return (
      <>
        <path d="M 140 95 Q 165 95 180 95" stroke="#1a1a1a" strokeWidth="3" fill="none" strokeLinecap="round" />
        <circle cx="180" cy="95" r="5" fill="#1a1a1a" />
      </>
    );
    if (isSad) return (
      <>
        <path d="M 140 95 Q 148 115 145 135" stroke="#1a1a1a" strokeWidth="3" fill="none" strokeLinecap="round" />
        <circle cx="145" cy="137" r="5" fill="#1a1a1a" />
      </>
    );
    if (isWaving) return (
      <>
        <path d="M 140 95 Q 160 75 165 50" stroke="#1a1a1a" strokeWidth="3" fill="none" strokeLinecap="round" />
        <circle cx="165" cy="48" r="5" fill="#1a1a1a" />
      </>
    );
    return (
      <>
        <path d="M 140 95 Q 158 85 162 70" stroke="#1a1a1a" strokeWidth="3" fill="none" strokeLinecap="round" />
        <circle cx="162" cy="68" r="5" fill="#1a1a1a" />
      </>
    );
  };

  const renderLeftArm = () => {
    // When holding a flag in the left hand, override the left arm to
    // reach the pole at y=72. (Right-hand flag uses the right arm.)
    if (flagOnLeft) {
      return (
        <>
          <path d="M 60 95 Q 48 85 45 72" stroke="#1a1a1a" strokeWidth="3" fill="none" strokeLinecap="round" />
          <circle cx="45" cy="70" r="5" fill="#1a1a1a" />
        </>
      );
    }
    if (armsUp) return (
      <>
        <path d="M 60 95 Q 40 70 35 40" stroke="#1a1a1a" strokeWidth="3" fill="none" strokeLinecap="round" />
        <circle cx="35" cy="38" r="5" fill="#1a1a1a" />
      </>
    );
    if (armsOut) return (
      <>
        <path d="M 60 95 Q 35 95 20 95" stroke="#1a1a1a" strokeWidth="3" fill="none" strokeLinecap="round" />
        <circle cx="20" cy="95" r="5" fill="#1a1a1a" />
      </>
    );
    if (isSad) return (
      <>
        <path d="M 60 95 Q 52 115 55 135" stroke="#1a1a1a" strokeWidth="3" fill="none" strokeLinecap="round" />
        <circle cx="55" cy="137" r="5" fill="#1a1a1a" />
      </>
    );
    if (isWaving) return (
      <>
        <path d="M 60 95 Q 48 110 52 130" stroke="#1a1a1a" strokeWidth="3" fill="none" strokeLinecap="round" />
        <circle cx="52" cy="132" r="5" fill="#1a1a1a" />
      </>
    );
    return (
      <>
        <path d="M 60 95 Q 42 85 38 70" stroke="#1a1a1a" strokeWidth="3" fill="none" strokeLinecap="round" />
        <circle cx="38" cy="68" r="5" fill="#1a1a1a" />
      </>
    );
  };

  // Dynamic flag sizing — width adapts so team names of any length fit.
  // Text is Inter bold at 14 user-units uppercase ≈ 9.5 per char.
  // The flag drapes outward from the pole; sign flips for right-hand grip
  // so the fabric extends away from the body (and away from a paired
  // partner standing on the inner side).
  const flagWidth = Math.max(70, Math.round(teamName.length * 9.5) + 18);
  const poleX = flagOnRight ? 155 : 45;
  const drape = flagOnRight ? 1 : -1;
  const nearX = poleX + drape * 1;
  const farX = poleX + drape * flagWidth;
  const curveX = farX + drape * 5;
  const pinchX = farX - drape * 2;
  const textX = Math.round((nearX + farX) / 2);
  const flagPath =
    `M ${nearX} 14 ` +
    `L ${farX} 18 ` +
    `Q ${curveX} 20 ${farX} 24 ` +
    `L ${pinchX} 34 ` +
    `Q ${curveX} 38 ${farX} 42 ` +
    `L ${nearX} 44 Z`;

  return (
    <div className={`celebration-dance-${dance}`}>
      <svg
        width={dimensions.width}
        height={dimensions.height}
        viewBox="0 0 200 260"
        xmlns="http://www.w3.org/2000/svg"
        style={{ overflow: 'visible' }}
      >
        {/* Ground shadow */}
        <ellipse cx="100" cy="246" rx="40" ry="4" fill="#000" opacity="0.08" />

        {/* Left leg */}
        <g className={running ? 'celebration-run-leg-left' : ''}>
          <line x1="88" y1="150" x2="82" y2="200" stroke="#1a1a1a" strokeWidth="3" strokeLinecap="round" />
          <ellipse cx="78" cy="210" rx="14" ry="6" fill="#1a1a1a" />
          <ellipse cx="78" cy="208" rx="11" ry="3.5" fill="#FFFFFF" />
        </g>

        {/* Right leg */}
        <g className={running ? 'celebration-run-leg-right' : ''}>
          <line x1="112" y1="150" x2="118" y2="200" stroke="#1a1a1a" strokeWidth="3" strokeLinecap="round" />
          <ellipse cx="122" cy="210" rx="14" ry="6" fill="#1a1a1a" />
          <ellipse cx="122" cy="208" rx="11" ry="3.5" fill="#FFFFFF" />
        </g>

        {renderRightArm()}
        {renderLeftArm()}

        {/* Body - round shape with team color */}
        <circle cx="100" cy="90" r="60" fill={color} opacity={isSad ? 0.78 : 1} />

        {/* Highlight for dimension */}
        <ellipse cx="82" cy="68" rx="18" ry="12" fill="#FFFFFF" opacity="0.25" />

        {/* Bottom shading */}
        <path
          d="M 50 110 Q 100 160 150 110 Q 150 150 100 150 Q 50 150 50 110 Z"
          fill="#000"
          opacity="0.08"
        />

        {/* Eyes */}
        {isSad ? (
          <>
            <path d="M 78 82 Q 84 88 90 82" stroke="#1a1a1a" strokeWidth="3" fill="none" strokeLinecap="round" />
            <path d="M 110 82 Q 116 88 122 82" stroke="#1a1a1a" strokeWidth="3" fill="none" strokeLinecap="round" />
          </>
        ) : isComposed ? (
          <>
            <path d="M 78 79 Q 84 76 90 79" stroke="#1a1a1a" strokeWidth="3" fill="none" strokeLinecap="round" />
            <path d="M 110 79 Q 116 76 122 79" stroke="#1a1a1a" strokeWidth="3" fill="none" strokeLinecap="round" />
          </>
        ) : (
          <>
            <path d="M 78 78 Q 84 72 90 78" stroke="#1a1a1a" strokeWidth="3" fill="none" strokeLinecap="round" />
            <path d="M 110 78 Q 116 72 122 78" stroke="#1a1a1a" strokeWidth="3" fill="none" strokeLinecap="round" />
          </>
        )}

        {/* Cheeks */}
        <circle cx="74" cy="95" r="5" fill="#FF9AA2" opacity={isSad ? 0.3 : isComposed ? 0.4 : 0.55} />
        <circle cx="126" cy="95" r="5" fill="#FF9AA2" opacity={isSad ? 0.3 : isComposed ? 0.4 : 0.55} />

        {/* Mouth */}
        {isSad ? (
          <path d="M 86 108 Q 100 96 114 108" stroke="#1a1a1a" strokeWidth="3" fill="none" strokeLinecap="round" />
        ) : isComposed ? (
          <path d="M 90 103 Q 100 107 110 103" stroke="#1a1a1a" strokeWidth="3" fill="none" strokeLinecap="round" />
        ) : (
          <>
            <path
              d="M 86 98 Q 100 112 114 98"
              stroke="#1a1a1a"
              strokeWidth="3"
              fill="#1a1a1a"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path d="M 89 100 Q 100 110 111 100 L 108 103 Q 100 108 92 103 Z" fill="#FFFFFF" />
          </>
        )}

        {/* Crown (Champions) */}
        {pose === 'crown' && (
          <g>
            <path
              d="M 72 30 L 78 18 L 88 28 L 100 14 L 112 28 L 122 18 L 128 30 L 124 38 L 76 38 Z"
              fill="#F9C151"
              stroke="#BB402A"
              strokeWidth="1.5"
              strokeLinejoin="round"
            />
            <circle cx="88" cy="32" r="2.5" fill="#EC5E59" />
            <circle cx="100" cy="30" r="3" fill="#3B7755" />
            <circle cx="112" cy="32" r="2.5" fill="#6B3FAF" />
          </g>
        )}

        {/* Flag held in right hand — width + text scale with team name.
            The outer <g> scales the whole flag 2.5x around the hand grip
            (poleX, 72) so it stands out for classroom visibility while
            the character body stays the same size. */}
        {showFlag && teamName && (
          <g transform={`translate(${poleX} 72) scale(2.5) translate(${-poleX} -72)`}>
            <line
              x1={poleX}
              y1="10"
              x2={poleX}
              y2="74"
              stroke="#1a1a1a"
              strokeWidth="2.5"
              strokeLinecap="round"
            />
            <circle cx={poleX} cy="10" r="3" fill="#1a1a1a" />
            <g
              className={
                droppingFlag
                  ? 'celebration-flag-dropping'
                  : (flagWaving && !isSad ? 'celebration-flag-wave' : '')
              }
              style={{ transformOrigin: `${poleX}px 29px` }}
            >
              <path
                d={flagPath}
                fill={color}
                stroke="#1a1a1a"
                strokeWidth="1.5"
                strokeLinejoin="round"
                opacity={isSad ? 0.75 : 1}
              />
              <text
                x={textX}
                y="34"
                textAnchor="middle"
                fontFamily="Inter, sans-serif"
                fontSize="14"
                fontWeight="800"
                fill={getContrastText(color)}
                letterSpacing="0.8"
              >
                {teamName.toUpperCase()}
              </text>
            </g>
          </g>
        )}
      </svg>
    </div>
  );
}
